import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import {
    Camera, X, Loader2, Sparkles, Check, AlertCircle,
    Image as ImageIcon, Share2, Download, Crown, Info,
    Instagram, Twitter, Upload, Wand2, RotateCcw, ChevronDown,
    Heart, Zap, User, Clock, Trash2, BookOpen, Plus, Bookmark
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { useToast } from '../components/ui/use-toast';
import { getApiUrl } from '../utils';

const API_URL = getApiUrl();

// TikTok icon component
const TikTokIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
);

// Curated collection of clothing items
// Category filters for try-on garments
const garmentCategories = [
    { id: 'all', label: 'All' },
    { id: 'tops', label: 'Tops' },
    { id: 'dresses', label: 'Dresses' },
    { id: 'outerwear', label: 'Outerwear' }
];

// Static curated collection - NO API CALLS needed (free!)
// These are flat-lay/product images that work well with virtual try-on
const curatedGarments = [
    {
        id: 'g1',
        name: 'Classic White Button Shirt',
        image: 'https://images.unsplash.com/photo-1598032895397-b9472444bf93?w=400&h=500&fit=crop',
        category: 'tops',
        displayCategory: 'TOPS',
        color: 'White'
    },
    {
        id: 'g2',
        name: 'Black Blazer',
        image: 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=400&h=500&fit=crop',
        category: 'outerwear',
        displayCategory: 'OUTERWEAR',
        color: 'Black'
    },
    {
        id: 'g3',
        name: 'Cream Knit Sweater',
        image: 'https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?w=400&h=500&fit=crop',
        category: 'tops',
        displayCategory: 'TOPS',
        color: 'Cream'
    },
    {
        id: 'g4',
        name: 'Floral Summer Dress',
        image: 'https://images.unsplash.com/photo-1572804013309-59a88b7e92f1?w=400&h=500&fit=crop',
        category: 'dresses',
        displayCategory: 'DRESSES',
        color: 'Floral'
    },
    {
        id: 'g5',
        name: 'Denim Jacket',
        image: 'https://images.unsplash.com/photo-1576995853123-5a10305d93c0?w=400&h=500&fit=crop',
        category: 'outerwear',
        displayCategory: 'OUTERWEAR',
        color: 'Blue Denim'
    },
    {
        id: 'g6',
        name: 'Navy Blouse',
        image: 'https://images.unsplash.com/photo-1564257631407-4deb1f99d992?w=400&h=500&fit=crop',
        category: 'tops',
        displayCategory: 'TOPS',
        color: 'Navy'
    },
    {
        id: 'g7',
        name: 'Elegant Black Dress',
        image: 'https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=400&h=500&fit=crop',
        category: 'dresses',
        displayCategory: 'DRESSES',
        color: 'Black'
    },
    {
        id: 'g8',
        name: 'Camel Wool Coat',
        image: 'https://images.unsplash.com/photo-1539533018447-63fcce2678e3?w=400&h=500&fit=crop',
        category: 'outerwear',
        displayCategory: 'OUTERWEAR',
        color: 'Camel'
    },
    {
        id: 'g9',
        name: 'Striped T-Shirt',
        image: 'https://images.unsplash.com/photo-1562157873-818bc0726f68?w=400&h=500&fit=crop',
        category: 'tops',
        displayCategory: 'TOPS',
        color: 'Blue/White Stripes'
    },
    {
        id: 'g10',
        name: 'Red Midi Dress',
        image: 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=400&h=500&fit=crop',
        category: 'dresses',
        displayCategory: 'DRESSES',
        color: 'Red'
    },
    {
        id: 'g11',
        name: 'Leather Jacket',
        image: 'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=400&h=500&fit=crop',
        category: 'outerwear',
        displayCategory: 'OUTERWEAR',
        color: 'Black Leather'
    },
    {
        id: 'g12',
        name: 'Pink Satin Blouse',
        image: 'https://images.unsplash.com/photo-1598554747436-c9293d6a588f?w=400&h=500&fit=crop',
        category: 'tops',
        displayCategory: 'TOPS',
        color: 'Pink'
    }
];

// Processing steps for the loader
const processingSteps = [
    { text: 'Analyzing your photo...', icon: User },
    { text: 'Detecting body pose...', icon: Sparkles },
    { text: 'Preparing garment...', icon: ImageIcon },
    { text: 'AI fitting in progress...', icon: Wand2 },
    { text: 'Finalizing your look...', icon: Zap }
];

export default function VirtualTryOn({ onClose, selectedGarment = null }) {
    // State
    const [userPhoto, setUserPhoto] = useState(null);
    const [userPhotoPreview, setUserPhotoPreview] = useState(null);
    const [hasSavedModel, setHasSavedModel] = useState(false);
    const [savedModelId, setSavedModelId] = useState(null);
    const [isLoadingModel, setIsLoadingModel] = useState(true);
    const [selectedItem, setSelectedItem] = useState(() => {
        if (selectedGarment) {
            return {
                ...selectedGarment,
                category: selectedGarment.category || 'tops',
                displayCategory: selectedGarment.displayCategory || selectedGarment.category?.toUpperCase() || 'CLOTHING'
            };
        }
        return null;
    });
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingStep, setProcessingStep] = useState(0);
    const [tryOnResult, setTryOnResult] = useState(null);
    const [tryOnHistory, setTryOnHistory] = useState([]);
    const [error, setError] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [showShareMenu, setShowShareMenu] = useState(false);
    const [activeTab, setActiveTab] = useState('wardrobe'); // wardrobe, history
    const [isFavorited, setIsFavorited] = useState(false);
    const [showWardrobeSheet, setShowWardrobeSheet] = useState(false); // bottom sheet popup on mobile

    // Photo quality validation state
    const [photoQuality, setPhotoQuality] = useState(null);
    const [isValidatingPhoto, setIsValidatingPhoto] = useState(false);
    
    // Category override — lets user correct auto-detected garment type
    const [categoryOverride, setCategoryOverride] = useState('auto');
    
    // Tips panel state
    const [showTips, setShowTips] = useState(false);

    // Lookbook state
    const [showLookbookDialog, setShowLookbookDialog] = useState(false);
    const [userLookbooks, setUserLookbooks] = useState([]);
    const [newLookbookName, setNewLookbookName] = useState('');
    const [loadingLookbooks, setLoadingLookbooks] = useState(false);
    const { toast } = useToast();
    
    // Wardrobe state
    const [wardrobeItems, setWardrobeItems] = useState([]);
    const [isLoadingWardrobe, setIsLoadingWardrobe] = useState(false);
    const [selectedOutfit, setSelectedOutfit] = useState({
        tops: null,
        bottoms: null,
        dresses: null,
        outerwear: null
    });
    
    // Use static curated garments - NO API COST!
    const [selectedCategory, setSelectedCategory] = useState('all');
    const isLoadingGarments = false; // Static data, no loading needed
    
    // Filter garments by category
    const filteredGarments = curatedGarments.filter(g => 
        selectedCategory === 'all' || g.category === selectedCategory
    );
    
    const fileInputRef = useRef(null);
    const token = localStorage.getItem('token');

    // Lookbook functions
    const fetchUserLookbooks = async () => {
        if (!token) return;
        setLoadingLookbooks(true);
        try {
            const res = await axios.get(`${API_URL}/lookbooks`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUserLookbooks(res.data.lookbooks || res.data || []);
        } catch (err) {
            console.error('❌ Failed to fetch lookbooks:', err);
        } finally {
            setLoadingLookbooks(false);
        }
    };

    const openAddToLookbook = () => {
        if (!tryOnResult) return;
        setShowLookbookDialog(true);
        fetchUserLookbooks();
    };

    const createLookbookAndAddTryon = async () => {
        if (!newLookbookName.trim() || !tryOnResult) return;
        try {
            const createRes = await axios.post(`${API_URL}/lookbooks`, {
                name: newLookbookName.trim(),
                description: 'Created from Virtual Try-On'
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const newLookbook = createRes.data.lookbook || createRes.data;
            await axios.post(`${API_URL}/lookbooks/${newLookbook.id}/items/custom`, {
                imageUrl: tryOnResult,
                title: selectedItem ? `Try-On: ${selectedItem.name || 'Result'}` : 'Try-On Result'
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast({ title: '✅ Added to lookbook', description: `Saved to "${newLookbookName.trim()}"` });
            setShowLookbookDialog(false);
            setNewLookbookName('');
        } catch (err) {
            console.error('❌ Failed to create lookbook:', err);
            toast({ title: '❌ Error', description: 'Failed to create lookbook', variant: 'destructive' });
        }
    };

    const addTryonToExistingLookbook = async (lookbookId, lookbookName) => {
        if (!tryOnResult) return;
        try {
            await axios.post(`${API_URL}/lookbooks/${lookbookId}/items/custom`, {
                imageUrl: tryOnResult,
                title: selectedItem ? `Try-On: ${selectedItem.name || 'Result'}` : 'Try-On Result'
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast({ title: '✅ Added to lookbook', description: `Saved to "${lookbookName}"` });
            setShowLookbookDialog(false);
        } catch (err) {
            console.error('❌ Failed to add to lookbook:', err);
            toast({ title: '❌ Error', description: 'Failed to add to lookbook', variant: 'destructive' });
        }
    };

    // Load wardrobe items
    useEffect(() => {
        const loadWardrobe = async () => {
            if (!token) return;
            setIsLoadingWardrobe(true);
            try {
                const res = await axios.get(`${API_URL}/real-wardrobe`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                // Filter to items that have images and are suitable for try-on
                const tryOnItems = (res.data.items || []).filter(item => 
                    item.image_url && ['tops', 'bottoms', 'dresses', 'outerwear'].includes(item.primary_category?.toLowerCase())
                );
                setWardrobeItems(tryOnItems);
            } catch (err) {
                console.error('Failed to load wardrobe:', err);
            } finally {
                setIsLoadingWardrobe(false);
            }
        };
        loadWardrobe();
    }, [token]);

    // Load saved model on mount
    useEffect(() => {
        const loadSavedModel = async () => {
            if (!token) {
                setIsLoadingModel(false);
                return;
            }
            try {
                const res = await axios.get(`${API_URL}/tryon/model`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data.hasModel && res.data.model) {
                    const model = res.data.model;
                    setHasSavedModel(true);
                    setSavedModelId(model.id);
                    if (model.photo_base64) {
                        const preview = `data:image/jpeg;base64,${model.photo_base64}`;
                        setUserPhotoPreview(preview);
                        setUserPhoto(model.photo_base64);
                    } else if (model.photo_url) {
                        setUserPhotoPreview(model.photo_url);
                        setUserPhoto(model.photo_url);
                    }
                }
            } catch (err) {
                console.error('Failed to load saved model:', err);
            } finally {
                setIsLoadingModel(false);
            }
        };
        loadSavedModel();
    }, [token]);

    // Simulate processing steps
    useEffect(() => {
        if (isProcessing) {
            const interval = setInterval(() => {
                setProcessingStep(prev => (prev + 1) % processingSteps.length);
            }, 2500);
            return () => clearInterval(interval);
        } else {
            setProcessingStep(0);
        }
    }, [isProcessing]);

    // Save model
    const saveAsModel = async () => {
        if (!userPhoto) return;
        try {
            setIsProcessing(true);
            const res = await axios.post(`${API_URL}/tryon/model`, {
                photoBase64: userPhoto,
                consent: true
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setHasSavedModel(true);
            setError(null);
            
            // Update photo quality from server response
            if (res.data.photoQuality) {
                setPhotoQuality(res.data.photoQuality);
                if (res.data.photoQuality.qualityScore < 50) {
                    toast({
                        title: '⚠️ Photo quality notice',
                        description: res.data.photoQuality.tips?.[0] || 'Consider uploading a higher quality photo for best results.',
                        variant: 'default'
                    });
                }
            }
        } catch (err) {
            if (err.response?.data?.photoQuality) {
                setPhotoQuality(err.response.data.photoQuality);
            }
            setError(err.response?.data?.message || 'Failed to save model. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    // Delete model
    const deleteModel = async () => {
        try {
            await axios.delete(`${API_URL}/tryon/model`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setHasSavedModel(false);
            setSavedModelId(null);
            setUserPhoto(null);
            setUserPhotoPreview(null);
            setTryOnResult(null);
        } catch (err) {
            setError('Failed to delete model.');
        }
    };

    // Drag handlers
    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0]);
        }
    }, []);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleFile(e.target.files[0]);
        }
    };

    const handleFile = (file) => {
        if (!file.type.startsWith('image/')) {
            setError('Please upload an image file (JPG, PNG)');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setError('Image must be less than 10MB');
            return;
        }
        
        setError(null);
        setHasSavedModel(false);
        setPhotoQuality(null);
        
        const reader = new FileReader();
        reader.onload = (event) => {
            const dataUrl = event.target.result;
            setUserPhotoPreview(dataUrl);
            const base64 = dataUrl.split(',')[1];
            setUserPhoto(base64);
            
            // Client-side dimension check first (instant)
            const img = new Image();
            img.onload = () => {
                const { naturalWidth: w, naturalHeight: h } = img;
                const aspectRatio = w / h;
                const clientWarnings = [];
                
                if (Math.min(w, h) < 400) {
                    clientWarnings.push('Photo resolution is very low — try-on quality may be poor.');
                }
                if (aspectRatio > 1.3) {
                    clientWarnings.push('Landscape orientation detected — portrait photos work much better.');
                }
                
                if (clientWarnings.length > 0) {
                    setPhotoQuality({
                        qualityScore: aspectRatio > 1.3 ? 40 : 50,
                        isGoodQuality: false,
                        warnings: clientWarnings,
                        tips: ['Take a vertical (portrait) photo, standing upright, head to at least hip visible.'],
                        width: w,
                        height: h
                    });
                }
            };
            img.src = dataUrl;
            
            // Also run server-side validation (more thorough — brightness, contrast)
            if (token) {
                setIsValidatingPhoto(true);
                axios.post(`${API_URL}/tryon/validate-photo`, { photoBase64: base64 }, {
                    headers: { Authorization: `Bearer ${token}` }
                }).then(res => {
                    setPhotoQuality(res.data);
                }).catch(err => {
                    console.warn('Photo validation failed (non-critical):', err.message);
                }).finally(() => {
                    setIsValidatingPhoto(false);
                });
            }
        };
        reader.readAsDataURL(file);
    };

    const clearPhoto = () => {
        setUserPhoto(null);
        setUserPhotoPreview(null);
        setTryOnResult(null);
        setHasSavedModel(false);
        setPhotoQuality(null);
    };

    const selectItem = (item) => {
        setSelectedItem(item);
        setTryOnResult(null);
        setCategoryOverride('auto'); // Reset override for new item
    };

    // Toggle wardrobe item in outfit (one per category)
    const toggleOutfitItem = (item) => {
        const category = item.primary_category?.toLowerCase() || item.category?.toLowerCase() || 'tops';
        setSelectedOutfit(prev => {
            if (prev[category]?.id === item.id) {
                // Deselect
                return { ...prev, [category]: null };
            }
            // Select (replaces previous in same category)
            return { ...prev, [category]: item };
        });
    };

    // Check if item is in outfit
    const isInOutfit = (item) => {
        const category = item.primary_category?.toLowerCase() || item.category?.toLowerCase() || 'tops';
        return selectedOutfit[category]?.id === item.id;
    };

    // Get outfit items array
    const getOutfitItems = () => {
        return Object.values(selectedOutfit).filter(Boolean);
    };

    // Generate try-on with outfit - picks the highest priority single item
    const selectOutfitForTryOn = async () => {
        const items = getOutfitItems();
        if (items.length === 0) {
            setError('Please select at least one item for your outfit');
            return;
        }
        
        if (!userPhoto) {
            setError('Please upload your photo first');
            return;
        }
        
        // Pick the highest priority single item
        const priorityOrder = ['tops', 'dresses', 'outerwear', 'bottoms'];
        let itemToTryOn = null;
        for (const cat of priorityOrder) {
            if (selectedOutfit[cat]) {
                itemToTryOn = selectedOutfit[cat];
                break;
            }
        }
        if (itemToTryOn) {
            const tryOnImage = itemToTryOn.original_image_url || itemToTryOn.image_url;
            const garmentItem = {
                id: `wardrobe-${itemToTryOn.id}`,
                name: itemToTryOn.name,
                image: tryOnImage,
                displayImage: itemToTryOn.image_url,
                category: itemToTryOn.primary_category?.toLowerCase() || 'tops',
                displayCategory: (itemToTryOn.primary_category || 'CLOTHING').toUpperCase(),
                color: itemToTryOn.color || 'Unknown'
            };
            selectItem(garmentItem);
            
            // Start try-on
            setTimeout(() => {
                generateTryOnWithItem(garmentItem);
            }, 100);
        }
    };

    // Generate try-on with a specific item (for auto-trigger)
    const generateTryOnWithItem = async (item) => {
        if (!userPhoto) {
            setError('Please upload your photo first');
            return;
        }
        if (!item) {
            setError('Please select an outfit to try on');
            return;
        }

        setIsProcessing(true);
        setError(null);

        try {
            // Save model if not saved
            if (!hasSavedModel) {
                await axios.post(`${API_URL}/tryon/model`, {
                    photoBase64: userPhoto,
                    consent: true
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setHasSavedModel(true);
            }

            // Generate try-on
            const res = await axios.post(`${API_URL}/tryon/generate`, {
                garmentImageUrl: item.image,
                garmentType: categoryOverride !== 'auto' ? categoryOverride : (item.category || 'tops'),
                garmentDescription: `${item.color || ''} ${item.name || ''}`.trim()
            }, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 150000
            });

            const resultUrl = res.data.resultImageUrl;
            setTryOnResult(resultUrl);

            // Add to history
            setTryOnHistory(prev => [{
                id: Date.now(),
                result: resultUrl,
                garment: item,
                timestamp: new Date()
            }, ...prev].slice(0, 10));
            
            setIsProcessing(false);
            
            if (res.data.provider) {
                console.log(`✅ Generated with: ${res.data.provider}`);
            }
        } catch (err) {
            console.error('Try-on error:', err);
            // Ensure errorMsg is always a string
            let errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'AI processing failed';
            if (typeof errorMsg !== 'string') {
                errorMsg = JSON.stringify(errorMsg) || 'AI processing failed';
            }
            
            if (err.response?.status === 429) {
                setError('⚠️ You\'ve reached your try-on limit. Please try again later.');
            } else if (err.response?.data?.retakeAdvice || errorMsg.includes('person') || errorMsg.includes('body') || errorMsg.includes('detect') || errorMsg.includes('pose') || errorMsg.includes('Pose')) {
                setError('📷 Body pose not detected — please retake your photo:\n• Stand facing the camera directly\n• Show head to at least hip\n• Good, even lighting\n• Arms at your sides\n• Plain background');
            } else if (err.code === 'ECONNABORTED') {
                setError('⏱️ Processing took too long. Please try again.');
            } else {
                setError(`Try-on failed: ${errorMsg}`);
            }
            setIsProcessing(false);
        }
    };

    // Generate try-on with Fashn AI
    const generateTryOn = async () => {
        if (!userPhoto) {
            setError('Please upload your photo first');
            return;
        }
        if (!selectedItem) {
            setError('Please select an outfit to try on');
            return;
        }

        setIsProcessing(true);
        setError(null);

        try {
            // Save model if not saved
            if (!hasSavedModel) {
                await axios.post(`${API_URL}/tryon/model`, {
                    photoBase64: userPhoto,
                    consent: true
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setHasSavedModel(true);
            }

            // Generate try-on
            const res = await axios.post(`${API_URL}/tryon/generate`, {
                garmentImageUrl: selectedItem.image,
                garmentType: categoryOverride !== 'auto' ? categoryOverride : (selectedItem.category || 'tops'),
                garmentDescription: `${selectedItem.color || ''} ${selectedItem.name || ''}`.trim()
            }, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 150000
            });

            const resultUrl = res.data.resultImageUrl;
            setTryOnResult(resultUrl);

            // Add to history
            setTryOnHistory(prev => [{
                id: Date.now(),
                result: resultUrl,
                garment: selectedItem,
                timestamp: new Date()
            }, ...prev].slice(0, 10));
            
            setIsProcessing(false);
            
            if (res.data.provider) {
                console.log(`✅ Generated with: ${res.data.provider}`);
            }
        } catch (err) {
            console.error('Try-on error:', err);
            // Ensure errorMsg is always a string
            let errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'AI processing failed';
            if (typeof errorMsg !== 'string') {
                errorMsg = JSON.stringify(errorMsg) || 'AI processing failed';
            }
            
            if (err.response?.status === 429) {
                setError('⚠️ You\'ve reached your try-on limit. Please try again later.');
            } else if (err.response?.data?.retakeAdvice || errorMsg.includes('person') || errorMsg.includes('body') || errorMsg.includes('detect') || errorMsg.includes('pose') || errorMsg.includes('Pose')) {
                setError('📷 Body pose not detected — please retake your photo:\n• Stand facing the camera directly\n• Show head to at least hip\n• Good, even lighting\n• Arms at your sides\n• Plain background');
            } else if (err.code === 'ECONNABORTED') {
                setError('⏱️ Processing took too long. Please try again.');
            } else {
                setError(`Try-on failed: ${errorMsg}`);
            }
            setIsProcessing(false);
        }
    };

    // Download result (iOS-compatible via Capacitor)
    const downloadResult = async () => {
        if (!tryOnResult) return;
        try {
            // Fetch image as base64
            const response = await fetch(tryOnResult);
            const blob = await response.blob();
            const reader = new FileReader();
            const base64 = await new Promise((resolve) => {
                reader.onloadend = () => resolve(reader.result.split(',')[1]);
                reader.readAsDataURL(blob);
            });

            const fileName = `fashiondisc-tryon-${Date.now()}.jpg`;
            const savedFile = await Filesystem.writeFile({
                path: fileName,
                data: base64,
                directory: Directory.Cache,
            });

            await Share.share({
                title: 'Virtual Try-On Result',
                url: savedFile.uri,
            });

            toast({ title: '✅ Ready to save!', description: 'Use the share sheet to save to Photos' });
        } catch (err) {
            console.error('Download failed:', err);
            // Fallback for web
            try {
                const link = document.createElement('a');
                link.download = `fashiondisc-tryon-${Date.now()}.jpg`;
                link.href = tryOnResult;
                link.click();
                toast({ title: '✅ Downloaded!' });
            } catch (e2) {
                toast({ title: '❌ Download failed', description: 'Could not save the image', variant: 'destructive' });
            }
        }
    };

    // Save result to gallery (backend saved items — makes it available in Style Studio canvas)
    const saveToGallery = async () => {
        if (!tryOnResult) return;
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                toast({ title: '❌ Not signed in', description: 'Please sign in to save', variant: 'destructive' });
                return;
            }
            const timestamp = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
            await axios.post(`${API_URL}/saved`, {
                product: {
                    name: `Virtual Try-On — ${timestamp}`,
                    brand: 'AI Studio',
                    image_url: tryOnResult,
                    store_url: '',
                    category: 'AI Studio',
                    style: 'tryon'
                },
                source: 'virtual-tryon'
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIsFavorited(true);
            toast({ title: '✅ Saved!', description: 'Try-on result saved — find it in Saved ❤️ page → Try-On section & Style Studio canvas' });
        } catch (e) {
            console.error('Save failed:', e);
            toast({ title: '❌ Could not save', description: e.response?.data?.message || 'Please try again', variant: 'destructive' });
        }
    };

    // Share handlers
    const shareToSocial = (platform) => {
        const shareText = "Check out my virtual try-on from fashionDisc! 👗✨ #FashionDisc #VirtualTryOn";
        const shareUrl = "https://fashiondisc.app";
        
        if (platform === 'twitter') {
            window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
        } else {
            downloadResult();
            alert(`Image downloaded! Open ${platform} and share your look! 📸`);
        }
        setShowShareMenu(false);
    };

    return (
        <>
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent 
                hideClose
                className="max-w-5xl w-full max-h-[100dvh] sm:max-h-[95vh] overflow-hidden p-0 bg-white border-0 rounded-none sm:rounded-3xl mx-0 sm:mx-auto top-0 sm:top-[50%] translate-y-0 sm:translate-y-[-50%] h-[100dvh] sm:h-auto"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 border-b border-stone-100" style={{ paddingTop: 'max(12px, env(safe-area-inset-top, 12px))' }}>
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500">
                            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                        </div>
                        <div>
                            <h1 className="text-sm sm:text-lg font-semibold text-stone-800">Virtual Try-On</h1>
                            <p className="text-[10px] sm:text-xs text-stone-500 hidden sm:block">Powered by Fashn AI</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-2">
                        {/* Mobile wardrobe toggle */}
                        <button
                            onClick={() => setShowWardrobeSheet(!showWardrobeSheet)}
                            className="lg:hidden px-3 py-2 rounded-full text-xs font-medium bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors flex items-center gap-1.5 relative"
                        >
                            <ImageIcon className="w-3.5 h-3.5" />
                            {showWardrobeSheet ? 'Close' : 'Wardrobe'}
                            {getOutfitItems().length > 0 && (
                                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                                    {getOutfitItems().length}
                                </span>
                            )}
                        </button>
                        <button 
                            onClick={onClose}
                            className="w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors"
                        >
                            <X className="w-5 h-5 text-stone-600" />
                        </button>
                    </div>
                </div>

                <div className="flex flex-col lg:flex-row h-[calc(100dvh-60px)] sm:h-[calc(95vh-80px)] overflow-y-auto lg:overflow-hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
                    {/* Left Panel - Photo & Result — always visible */}
                    <div className="flex-1 p-3 sm:p-6 flex flex-col pb-24 sm:pb-6 lg:pb-6">
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                            {/* Your Photo */}
                            <div className="flex flex-col">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-sm font-medium text-stone-700">Your Photo</span>
                                    {hasSavedModel && (
                                        <span className="text-xs bg-olive-100 text-olive-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                            <Check className="w-3 h-3" /> Saved
                                        </span>
                                    )}
                                </div>
                                
                                {isLoadingModel ? (
                                    <div className="flex-1 rounded-2xl bg-stone-50 flex items-center justify-center min-h-[200px] sm:min-h-[300px]">
                                        <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
                                    </div>
                                ) : userPhotoPreview ? (
                                    <div className="flex-1 relative rounded-2xl overflow-hidden bg-stone-100 group min-h-[200px] sm:min-h-[300px]">
                                        <img 
                                            src={userPhotoPreview} 
                                            alt="Your photo" 
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                            {!hasSavedModel && (
                                                <button
                                                    onClick={saveAsModel}
                                                    className="px-3 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 transition-colors flex items-center gap-1"
                                                >
                                                    <Check className="w-4 h-4" />
                                                    Save
                                                </button>
                                            )}
                                            <button
                                                onClick={hasSavedModel ? deleteModel : clearPhoto}
                                                className="px-3 py-2 bg-white text-stone-700 text-sm font-medium rounded-lg hover:bg-stone-100 transition-colors flex items-center gap-1"
                                            >
                                                <RotateCcw className="w-4 h-4" />
                                                Change
                                            </button>
                                        </div>
                                        {/* Status Badge */}
                                        {hasSavedModel && (
                                            <div className="absolute bottom-3 left-3 right-3">
                                                <div className="bg-gradient-to-r from-olive-500 to-olive-400 text-white text-xs font-medium px-3 py-2 rounded-lg flex items-center gap-2">
                                                    <Sparkles className="w-4 h-4" />
                                                    Your Virtual Model
                                                </div>
                                            </div>
                                        )}
                                        {/* Photo Quality Indicator */}
                                        {photoQuality && !hasSavedModel && (
                                            <div className="absolute bottom-3 left-3 right-3">
                                                <div className={`text-xs font-medium px-3 py-2 rounded-lg flex items-start gap-2 ${
                                                    photoQuality.qualityScore >= 70
                                                        ? 'bg-green-500/90 text-white'
                                                        : photoQuality.qualityScore >= 50
                                                        ? 'bg-amber-500/90 text-white'
                                                        : 'bg-red-500/90 text-white'
                                                }`}>
                                                    {photoQuality.qualityScore >= 70 ? (
                                                        <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                                    ) : (
                                                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                                    )}
                                                    <div>
                                                        {photoQuality.qualityScore >= 70 
                                                            ? 'Great photo for try-on!'
                                                            : photoQuality.warnings?.[0] || 'Photo may not produce ideal results'
                                                        }
                                                        {photoQuality.qualityScore < 70 && photoQuality.tips?.[0] && (
                                                            <p className="opacity-80 mt-0.5">{photoQuality.tips[0]}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {isValidatingPhoto && (
                                            <div className="absolute top-3 right-3">
                                                <div className="bg-black/50 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                    Checking quality...
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div
                                        onDragEnter={handleDrag}
                                        onDragLeave={handleDrag}
                                        onDragOver={handleDrag}
                                        onDrop={handleDrop}
                                        onClick={() => fileInputRef.current?.click()}
                                        className={`
                                            flex-1 rounded-2xl border-2 border-dashed cursor-pointer min-h-[180px] sm:min-h-[300px]
                                            flex flex-col items-center justify-center gap-2 sm:gap-3
                                            transition-all duration-200
                                            ${dragActive 
                                                ? 'border-amber-500 bg-amber-50' 
                                                : 'border-stone-200 bg-stone-50 hover:border-amber-400 hover:bg-amber-50/50'
                                            }
                                        `}
                                    >
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            className="hidden"
                                        />
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                                            <Upload className="w-5 h-5 sm:w-7 sm:h-7 text-amber-600" />
                                        </div>
                                        <div className="text-center">
                                            <p className="font-medium text-stone-700 text-sm">Upload your photo</p>
                                            <p className="text-xs text-stone-400 mt-0.5 sm:mt-1">Portrait, front-facing, head to hip</p>
                                        </div>
                                        <p className="text-xs text-stone-400 flex items-center gap-1 hidden sm:flex">
                                            <ImageIcon className="w-3 h-3" />
                                            JPG, PNG up to 10MB
                                        </p>
                                        <div className="text-xs text-stone-400 text-center space-y-0.5 mt-1 hidden sm:block">
                                            <p>📐 Portrait orientation</p>
                                            <p>💡 Even lighting, no shadows</p>
                                            <p>🧍 Arms at sides, face camera</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Result */}
                            <div className="flex flex-col">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-sm font-medium text-stone-700">Try-On Result</span>
                                    {tryOnResult && (
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={saveToGallery}
                                                className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                                                    isFavorited ? 'bg-suede-100 text-suede-600' : 'bg-stone-100 text-stone-400 hover:text-suede-500'
                                                }`}
                                                title="Save to gallery"
                                            >
                                                <Bookmark className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
                                            </button>
                                            <button
                                                onClick={downloadResult}
                                                className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 hover:text-amber-600 transition-colors"
                                            >
                                                <Download className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={openAddToLookbook}
                                                className="w-7 h-7 rounded-full bg-terracotta-100 flex items-center justify-center text-terracotta-500 hover:bg-terracotta-200 transition-colors"
                                                title="Add to Lookbook"
                                            >
                                                <BookOpen className="w-4 h-4" />
                                            </button>
                                            <div className="relative">
                                                <button
                                                    onClick={() => setShowShareMenu(!showShareMenu)}
                                                    className="w-7 h-7 rounded-full bg-gradient-to-r from-terracotta-500 to-stone-600 flex items-center justify-center text-white"
                                                >
                                                    <Share2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="flex-1 rounded-2xl bg-gradient-to-br from-stone-50 to-amber-50/50 flex items-center justify-center relative overflow-hidden min-h-[200px] sm:min-h-[300px]">
                                    <AnimatePresence mode="wait">
                                        {isProcessing ? (
                                            <motion.div
                                                key="processing"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                className="text-center px-6"
                                            >
                                                <div className="relative mb-6">
                                                    <div className="w-20 h-20 rounded-full bg-gradient-to-r from-amber-400 via-orange-500 to-terracotta-500 flex items-center justify-center mx-auto animate-pulse">
                                                        {React.createElement(processingSteps[processingStep].icon, { 
                                                            className: "w-8 h-8 text-white" 
                                                        })}
                                                    </div>
                                                    <div className="absolute -inset-2 rounded-full border-4 border-amber-200/50 animate-ping" />
                                                </div>
                                                <p className="text-stone-700 font-medium mb-1">
                                                    {processingSteps[processingStep].text}
                                                </p>
                                                <p className="text-xs text-stone-400 mb-4">
                                                    Quality mode — about 15-25 seconds
                                                </p>
                                                {/* Progress dots */}
                                                <div className="flex items-center justify-center gap-1.5">
                                                    {processingSteps.map((_, i) => (
                                                        <div 
                                                            key={i} 
                                                            className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                                                i <= processingStep ? 'bg-amber-500 scale-110' : 'bg-stone-200'
                                                            }`} 
                                                        />
                                                    ))}
                                                </div>
                                            </motion.div>
                                        ) : tryOnResult ? (
                                            <motion.div
                                                key="result"
                                                initial={{ opacity: 0, scale: 0.95 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                className="w-full h-full"
                                            >
                                                <img 
                                                    src={tryOnResult} 
                                                    alt="Try-on result" 
                                                    className="w-full h-full object-cover rounded-2xl"
                                                />
                                            </motion.div>
                                        ) : (
                                            <motion.div
                                                key="empty"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="text-center px-6 overflow-y-auto max-h-full"
                                            >
                                                <div className="w-14 h-14 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-3">
                                                    <Wand2 className="w-6 h-6 text-stone-300" />
                                                </div>
                                                <p className="text-stone-500 text-sm font-medium mb-1">
                                                    {!userPhoto 
                                                        ? 'Upload a photo to get started'
                                                        : (!selectedItem && getOutfitItems().length === 0)
                                                            ? 'Select an outfit to try on'
                                                            : 'Click "Generate" to see the magic'
                                                    }
                                                </p>

                                                {/* Best Practices Guide */}
                                                <div className="mt-4 text-left bg-amber-50/70 rounded-xl p-4 border border-amber-100">
                                                    <p className="text-xs font-semibold text-amber-700 mb-2.5 flex items-center gap-1.5">
                                                        <Sparkles className="w-3.5 h-3.5" />
                                                        Tips for best results
                                                    </p>
                                                    <div className="space-y-2 text-xs text-stone-600">
                                                        <div className="flex items-start gap-2">
                                                            <Camera className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                                                            <span><strong>Your Photo:</strong> Portrait orientation, front-facing, head to at least hip visible. Arms relaxed at your sides.</span>
                                                        </div>
                                                        <div className="flex items-start gap-2">
                                                            <Zap className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                                                            <span><strong>Lighting:</strong> Even, natural daylight is ideal. Avoid harsh shadows, backlight, or flash.</span>
                                                        </div>
                                                        <div className="flex items-start gap-2">
                                                            <User className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                                                            <span><strong>Background:</strong> A clean, uncluttered background helps the AI focus on you.</span>
                                                        </div>
                                                        <div className="flex items-start gap-2">
                                                            <ImageIcon className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                                                            <span><strong>Garment Photo:</strong> Flat-lay or product-style photos give the most accurate results.</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                    
                                    {/* Share Menu */}
                                    <AnimatePresence>
                                        {showShareMenu && tryOnResult && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -10 }}
                                                className="absolute top-12 right-2 bg-white rounded-xl shadow-xl p-2 z-50 min-w-[140px]"
                                            >
                                                <button
                                                    onClick={() => shareToSocial('instagram')}
                                                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-stone-50 rounded-lg text-sm"
                                                >
                                                    <Instagram className="w-4 h-4 text-terracotta-500" /> Instagram
                                                </button>
                                                <button
                                                    onClick={() => shareToSocial('tiktok')}
                                                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-stone-50 rounded-lg text-sm"
                                                >
                                                    <TikTokIcon className="w-4 h-4" /> TikTok
                                                </button>
                                                <button
                                                    onClick={() => shareToSocial('twitter')}
                                                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-stone-50 rounded-lg text-sm"
                                                >
                                                    <Twitter className="w-4 h-4 text-stone-500" /> Twitter
                                                </button>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>
                        </div>

                        {/* Generate Button */}
                        <div className="mt-3 sm:mt-4 pb-2 lg:pb-0">
                            <AnimatePresence>
                                {error && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="mb-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl px-4 py-3"
                                    >
                                        <div className="flex items-start gap-2">
                                            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                            <div className="flex-1 whitespace-pre-line">{error}</div>
                                            <button onClick={() => setError(null)} className="flex-shrink-0">
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                        {(error.includes('retake') || error.includes('pose') || error.includes('Body pose')) && (
                                            <button 
                                                onClick={() => { setError(null); hasSavedModel ? deleteModel() : clearPhoto(); }}
                                                className="mt-2 text-xs bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1.5 rounded-lg font-medium transition-colors flex items-center gap-1"
                                            >
                                                <Camera className="w-3 h-3" />
                                                Upload a new photo
                                            </button>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                            
                            {/* Collapsible tips banner */}
                            <button
                                onClick={() => setShowTips(!showTips)}
                                className="w-full mb-2 flex items-center justify-between text-xs text-amber-600 hover:text-amber-700 transition-colors px-1"
                            >
                                <span className="flex items-center gap-1">
                                    <Info className="w-3.5 h-3.5" />
                                    How to get the best results
                                </span>
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showTips ? 'rotate-180' : ''}`} />
                            </button>
                            <AnimatePresence>
                                {showTips && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="mb-3 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 text-xs text-stone-600 space-y-1.5 overflow-hidden"
                                    >
                                        <p>📸 <strong>Photo:</strong> Portrait, front-facing, head to hip, arms at sides</p>
                                        <p>💡 <strong>Lighting:</strong> Even natural light — no shadows, backlight, or flash</p>
                                        <p>🧹 <strong>Background:</strong> Plain & uncluttered for best AI detection</p>
                                        <p>👗 <strong>Garment:</strong> Flat-lay or product photos work best</p>
                                        <p>📐 <strong>Resolution:</strong> At least 800×1200px for sharp results</p>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Garment Category Selector — helps AI pick the right fitting */}
                            {(selectedItem || getOutfitItems().length > 0) && !isProcessing && (
                                <div className="mb-2">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] text-stone-400 uppercase tracking-wider font-medium">Type:</span>
                                        {[
                                            { id: 'auto', label: 'Auto' },
                                            { id: 'tops', label: 'Top' },
                                            { id: 'bottoms', label: 'Bottom' },
                                            { id: 'one-pieces', label: 'Dress' },
                                            { id: 'outerwear', label: 'Outerwear' },
                                        ].map(cat => (
                                            <button
                                                key={cat.id}
                                                onClick={() => setCategoryOverride(cat.id)}
                                                className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all border ${
                                                    categoryOverride === cat.id
                                                        ? 'bg-amber-500 text-white border-amber-400 shadow-sm'
                                                        : 'bg-white text-stone-500 border-stone-200 hover:border-amber-300'
                                                }`}
                                            >
                                                {cat.label}
                                            </button>
                                        ))}
                                    </div>
                                    <p className="text-[10px] text-stone-400 mt-1.5 italic">✨ Best results: try one item at a time — tops & bottoms work best</p>
                                </div>
                            )}

                            {/* Generate Button */}
                            <div className="flex items-center gap-2">
                            <Button
                                onClick={() => {
                                    // If wardrobe outfit items are selected, use outfit flow
                                    if (!selectedItem && getOutfitItems().length > 0) {
                                        selectOutfitForTryOn();
                                    } else {
                                        generateTryOn();
                                    }
                                }}
                                disabled={!userPhoto || (!selectedItem && getOutfitItems().length === 0) || isProcessing}
                                className={`
                                    flex-1 py-4 sm:py-6 text-sm sm:text-base font-semibold rounded-xl transition-all
                                    ${userPhoto && (selectedItem || getOutfitItems().length > 0) && !isProcessing
                                        ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/25'
                                        : 'bg-stone-100 text-stone-400 cursor-not-allowed'
                                    }
                                `}
                            >
                                {isProcessing ? (
                                    <span className="flex items-center gap-2">
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Generating...
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2">
                                        <Wand2 className="w-5 h-5" />
                                        {!selectedItem && getOutfitItems().length > 0 
                                            ? `Try On ${getOutfitItems().length > 1 ? `${getOutfitItems().length} Items` : 'Outfit'}`
                                            : 'Generate Try-On'}
                                    </span>
                                )}
                            </Button>
                            </div>
                            {/* Spacer for mobile to ensure button isn't hidden behind floating bar */}
                            <div className="h-4 sm:h-0 lg:hidden flex-shrink-0" />
                        </div>

                        {/* Mobile floating selection bar — shows when items selected but sheet is closed */}
                        {!showWardrobeSheet && getOutfitItems().length > 0 && (
                            <div className="lg:hidden fixed left-3 right-3 z-40 bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.15)] border border-stone-200 p-3" style={{ bottom: 'max(16px, env(safe-area-inset-bottom, 16px))' }}>
                                <div className="flex items-center gap-3">
                                    <div className="flex -space-x-2 flex-shrink-0">
                                        {getOutfitItems().slice(0, 3).map(item => (
                                            <div key={item.id} className="w-10 h-10 rounded-lg overflow-hidden border-2 border-white shadow-sm">
                                                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-stone-800">{getOutfitItems().length} {getOutfitItems().length === 1 ? 'item' : 'items'} selected</p>
                                        <button 
                                            onClick={() => setShowWardrobeSheet(true)}
                                            className="text-xs text-amber-600 font-medium"
                                        >
                                            Edit selection
                                        </button>
                                    </div>
                                    <Button
                                        onClick={() => {
                                            if (!selectedItem && getOutfitItems().length > 0) {
                                                selectOutfitForTryOn();
                                            } else {
                                                generateTryOn();
                                            }
                                        }}
                                        disabled={!userPhoto || isProcessing}
                                        size="sm"
                                        className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl px-4 py-2 text-xs font-semibold shadow-lg shadow-amber-500/20 disabled:opacity-50"
                                    >
                                        {isProcessing ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <>
                                                <Wand2 className="w-3.5 h-3.5 mr-1" />
                                                Generate {getOutfitItems().length}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Backdrop overlay when wardrobe sheet is open on mobile */}
                    {showWardrobeSheet && (
                        <div 
                            className="fixed inset-0 bg-black/30 z-40 lg:hidden"
                            onClick={() => setShowWardrobeSheet(false)}
                        />
                    )}

                    {/* Right Panel - Outfits (bottom sheet on mobile, side panel on desktop) */}
                    <div className={`
                        lg:relative lg:w-80 lg:border-l border-stone-100 lg:flex lg:flex-col bg-stone-50/50
                        ${showWardrobeSheet 
                            ? 'fixed inset-x-0 bottom-0 z-50 flex flex-col bg-white rounded-t-2xl shadow-[0_-8px_30px_rgba(0,0,0,0.15)] lg:static lg:rounded-none lg:shadow-none' 
                            : 'hidden lg:flex lg:flex-col'
                        }
                    `}
                    style={showWardrobeSheet ? { height: '70vh', maxHeight: '70vh' } : {}}
                    >
                        {/* Mobile sheet handle + header */}
                        <div className="lg:hidden">
                            <div className="flex justify-center pt-2 pb-1">
                                <div className="w-10 h-1 rounded-full bg-stone-300" />
                            </div>
                            <div className="flex items-center justify-between px-4 pb-2">
                                <h3 className="text-sm font-semibold text-stone-700 flex items-center gap-2">
                                    <ImageIcon className="w-4 h-4 text-amber-500" />
                                    Select Outfit
                                    {getOutfitItems().length > 0 && (
                                        <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full font-semibold">
                                            {getOutfitItems().length} selected
                                        </span>
                                    )}
                                </h3>
                                <button
                                    onClick={() => setShowWardrobeSheet(false)}
                                    className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 hover:bg-stone-200"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        {/* Tabs */}
                        <div className="flex border-b border-stone-100">
                            <button
                                onClick={() => setActiveTab('wardrobe')}
                                className={`flex-1 py-3 text-sm font-medium transition-colors relative ${
                                    activeTab === 'wardrobe' 
                                        ? 'text-amber-600 border-b-2 border-amber-500' 
                                        : 'text-stone-400 hover:text-stone-600'
                                }`}
                            >
                                My Wardrobe
                                {wardrobeItems.length > 0 && (
                                    <span className="ml-1 text-xs bg-stone-200 text-stone-600 px-1.5 rounded-full">
                                        {wardrobeItems.length}
                                    </span>
                                )}
                            </button>
                            <button
                                onClick={() => setActiveTab('history')}
                                className={`flex-1 py-3 text-sm font-medium transition-colors relative ${
                                    activeTab === 'history' 
                                        ? 'text-amber-600 border-b-2 border-amber-500' 
                                        : 'text-stone-400 hover:text-stone-600'
                                }`}
                            >
                                History
                                {tryOnHistory.length > 0 && (
                                    <span className="ml-1 text-xs bg-amber-100 text-amber-600 px-1.5 rounded-full">
                                        {tryOnHistory.length}
                                    </span>
                                )}
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-4">
                            {activeTab === 'wardrobe' ? (
                                <>
                                    {/* Outfit Builder Header */}
                                    <div className="mb-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <h3 className="text-sm font-medium text-stone-700">Build Your Outfit</h3>
                                            {getOutfitItems().length > 0 && (
                                                <button 
                                                    onClick={() => setSelectedOutfit({ tops: null, bottoms: null, dresses: null, outerwear: null })}
                                                    className="text-xs text-stone-400 hover:text-stone-600"
                                                >
                                                    Clear
                                                </button>
                                            )}
                                        </div>
                                        <p className="text-xs text-stone-400">Select one item per category to create an outfit</p>
                                        
                                        {/* Selected Outfit Preview */}
                                        {getOutfitItems().length > 0 && (
                                            <div className="mt-3 p-3 bg-white rounded-xl border border-stone-200">
                                                <div className="flex gap-2 mb-2">
                                                    {getOutfitItems().map(item => (
                                                        <div key={item.id} className="relative w-12 h-12 rounded-lg overflow-hidden">
                                                            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                                            <button 
                                                                onClick={() => toggleOutfitItem(item)}
                                                                className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center"
                                                            >
                                                                <X className="w-3 h-3 text-white" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                                <Button
                                                    onClick={() => { selectOutfitForTryOn(); setShowWardrobeSheet(false); }}
                                                    size="sm"
                                                    disabled={isProcessing || !userPhoto}
                                                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs disabled:opacity-50"
                                                >
                                                    {isProcessing ? (
                                                        <>
                                                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                            {getOutfitItems().length > 1 ? 'Processing outfit...' : 'Generating...'}
                                                        </>
                                                    ) : !userPhoto ? (
                                                        <>
                                                            <Camera className="w-3 h-3 mr-1" />
                                                            Upload Photo First
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Sparkles className="w-3 h-3 mr-1" />
                                                            Try On {getOutfitItems().length > 1 ? `${getOutfitItems().length} Items` : 'Outfit'}
                                                        </>
                                                    )}
                                                </Button>
                                                {getOutfitItems().length > 1 && !isProcessing && (
                                                    <p className="text-[10px] text-stone-600 text-center mt-1">
                                                        ✨ Multi-item try-on enabled
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Wardrobe Items by Category */}
                                    {isLoadingWardrobe ? (
                                        <div className="flex items-center justify-center py-12">
                                            <div className="text-center">
                                                <Loader2 className="w-8 h-8 animate-spin text-stone-500 mx-auto mb-2" />
                                                <p className="text-sm text-stone-500">Loading your wardrobe...</p>
                                            </div>
                                        </div>
                                    ) : wardrobeItems.length === 0 ? (
                                        <div className="text-center py-12">
                                            <div className="w-16 h-16 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-4">
                                                <Camera className="w-7 h-7 text-stone-300" />
                                            </div>
                                            <p className="text-stone-500 text-sm mb-2">No wardrobe items yet</p>
                                            <p className="text-xs text-stone-400">Add clothes to your wardrobe to try them on</p>
                                        </div>
                                    ) : (
                                        <>
                                            {['tops', 'bottoms', 'dresses', 'outerwear'].map(category => {
                                                const categoryItems = wardrobeItems.filter(item => 
                                                    (item.primary_category?.toLowerCase() || '') === category
                                                );
                                                if (categoryItems.length === 0) return null;
                                                return (
                                                    <div key={category} className="mb-4">
                                                        <h4 className="text-xs font-semibold text-stone-500 uppercase mb-2">
                                                            {category} ({categoryItems.length})
                                                        </h4>
                                                        <div className="grid grid-cols-3 lg:grid-cols-2 gap-2">
                                                            {categoryItems.map(item => (
                                                                <motion.div
                                                                    key={item.id}
                                                                    whileHover={{ scale: 1.02 }}
                                                                    whileTap={{ scale: 0.98 }}
                                                                    onClick={() => toggleOutfitItem(item)}
                                                                    className={`
                                                                        cursor-pointer rounded-xl overflow-hidden bg-white
                                                                        transition-all duration-200 relative
                                                                        ${isInOutfit(item)
                                                                            ? 'ring-2 ring-stone-600 shadow-lg' 
                                                                            : 'hover:shadow-md border border-stone-100'
                                                                        }
                                                                    `}
                                                                >
                                                                    {isInOutfit(item) && (
                                                                        <div className="absolute top-1 right-1 z-10 w-5 h-5 bg-stone-600 rounded-full flex items-center justify-center">
                                                                            <Check className="w-3 h-3 text-white" />
                                                                        </div>
                                                                    )}
                                                                    <div className="aspect-square relative overflow-hidden bg-stone-100">
                                                                        <img 
                                                                            src={item.image_url} 
                                                                            alt={item.name}
                                                                            className="w-full h-full object-cover"
                                                                        />
                                                                    </div>
                                                                    <div className="p-2">
                                                                        <p className="text-xs font-medium text-stone-700 truncate">{item.name}</p>
                                                                        {item.color && (
                                                                            <p className="text-[10px] text-stone-400">{item.color}</p>
                                                                        )}
                                                                    </div>
                                                                </motion.div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </>
                                    )}
                                </>
                            ) : (
                                <div className="space-y-3">
                                    {tryOnHistory.length === 0 ? (
                                        <div className="text-center py-12">
                                            <div className="w-14 h-14 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-3">
                                                <Clock className="w-6 h-6 text-stone-300" />
                                            </div>
                                            <p className="text-sm text-stone-500 font-medium">No try-ons yet</p>
                                            <p className="text-xs text-stone-400 mt-1">Your history will appear here</p>
                                        </div>
                                    ) : (
                                        tryOnHistory.map((item) => (
                                            <motion.div
                                                key={item.id}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className="bg-white rounded-xl overflow-hidden shadow-sm group"
                                            >
                                                <div className="relative">
                                                    <img 
                                                        src={item.result} 
                                                        alt="Previous try-on"
                                                        className="w-full aspect-[4/5] object-cover"
                                                    />
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                        <button
                                                            onClick={() => {
                                                                const link = document.createElement('a');
                                                                link.download = `tryon-${item.id}.jpg`;
                                                                link.href = item.result;
                                                                link.click();
                                                            }}
                                                            className="p-2 bg-white rounded-full text-stone-700 hover:bg-stone-100"
                                                        >
                                                            <Download className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="p-2 flex items-center justify-between">
                                                    <div>
                                                        <p className="text-xs font-medium text-stone-700 truncate">
                                                            {item.garment?.name}
                                                        </p>
                                                        <p className="text-[10px] text-stone-400">
                                                            {new Date(item.timestamp).toLocaleDateString()}
                                                        </p>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Selected Item Info */}
                        {selectedItem && (
                            <div className="p-4 border-t border-stone-100 bg-white">
                                <div className="flex items-center gap-3">
                                    <img 
                                        src={selectedItem.displayImage || selectedItem.image} 
                                        alt={selectedItem.name}
                                        className="w-12 h-12 rounded-lg object-cover"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-stone-800 text-sm truncate">{selectedItem.name}</p>
                                        <p className="text-xs text-amber-600">{selectedItem.displayCategory}</p>
                                    </div>
                                    <button
                                        onClick={() => setSelectedItem(null)}
                                        className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center text-stone-400 hover:text-stone-600 hover:bg-stone-200 transition-colors"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>

        {/* Add to Lookbook Dialog */}
        <Dialog open={showLookbookDialog} onOpenChange={setShowLookbookDialog}>
            <DialogContent className="sm:max-w-md">
                <div className="space-y-4">
                    <div className="flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-terracotta-500" />
                        <h3 className="text-lg font-semibold text-stone-800">Add to Lookbook</h3>
                    </div>

                    {loadingLookbooks ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                        </div>
                    ) : (
                        <>
                            {userLookbooks.length > 0 && (
                                <div className="space-y-2">
                                    <p className="text-sm text-stone-500">Choose a lookbook:</p>
                                    <div className="max-h-48 overflow-y-auto space-y-1">
                                        {userLookbooks.map(lb => (
                                            <button
                                                key={lb.id}
                                                onClick={() => addTryonToExistingLookbook(lb.id, lb.name)}
                                                className="w-full text-left px-3 py-2 rounded-lg hover:bg-stone-100 transition-colors flex items-center gap-2"
                                            >
                                                <BookOpen className="w-4 h-4 text-terracotta-400" />
                                                <span className="text-sm text-stone-700">{lb.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="border-t pt-3">
                                <p className="text-sm text-stone-500 mb-2">Or create a new lookbook:</p>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={newLookbookName}
                                        onChange={(e) => setNewLookbookName(e.target.value)}
                                        placeholder="Lookbook name..."
                                        className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-300"
                                        onKeyDown={(e) => e.key === 'Enter' && createLookbookAndAddTryon()}
                                    />
                                    <Button
                                        onClick={createLookbookAndAddTryon}
                                        disabled={!newLookbookName.trim()}
                                        size="sm"
                                        className="bg-terracotta-500 hover:bg-terracotta-600 text-white"
                                    >
                                        <Plus className="w-4 h-4 mr-1" />
                                        Create
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
        </>
    );
}
